import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDailyTest, getDailyTestHistory } from '../../api';

// The Daily Test: one 90-question paper a day, built automatically at 4 am from
// the chapters that have been marked complete. It deliberately gives back the
// questions he got wrong and the ones he skipped, then tops up with fresh ones.

function ScorePill({ pct }) {
  if (pct == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const cls = pct >= 80 ? 'badge-green' : pct < 50 ? 'badge-red' : 'badge-yellow';
  return <span className={`badge ${cls}`}>{Math.round(pct)}%</span>;
}

function Made({ made }) {
  if (!made) return null;
  const bits = [
    { n: made.wrong_before, label: 'you got wrong before', colour: 'var(--danger)' },
    { n: made.left_unattempted, label: 'you left unanswered', colour: 'var(--warning)' },
    { n: made.newly_written, label: 'brand new', colour: 'var(--primary)' },
  ].filter(b => b.n > 0);
  if (!bits.length) return null;
  return (
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14 }}>
      {bits.map(b => (
        <div key={b.label}>
          <div style={{ fontSize: 22, fontWeight: 700, color: b.colour }}>{b.n}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function DailyTest() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['dailyTest'],
    queryFn: () => getDailyTest().then(r => r.data),
  });
  const { data: hist } = useQuery({
    queryKey: ['dailyTestHistory'],
    queryFn: () => getDailyTestHistory().then(r => r.data),
  });

  const past = (hist?.daily_tests || []).filter(d => d.status === 'completed');

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28 }}>📅 Daily Test</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          A fresh 90-question paper every day, built from the chapters you have finished.
          It brings back what you got wrong and what you skipped, and adds new questions
          written in real exam language.
        </p>
      </div>

      {isLoading ? <div className="spinner"></div> : !data?.available ? (
        <div className="card">
          <div className="empty-state" style={{ padding: '28px 0' }}>{data?.message || 'No test today.'}</div>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {new Date(data.date).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              <h2 style={{ fontSize: 20, marginTop: 2 }}>{data.title}</h2>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                {data.total_questions} questions · {data.time_limit_minutes} minutes
              </div>
            </div>
            {data.status === 'completed' ? (
              <button className="btn btn-ghost" onClick={() => navigate(`/student/test/${data.test_id}/result`)}>
                See result
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => navigate(`/student/test/${data.test_id}`)}>
                Start today's test
              </button>
            )}
          </div>

          {data.status === 'completed' && data.result && (
            <div style={{ marginTop: 14, display: 'flex', gap: 18, alignItems: 'center' }}>
              <ScorePill pct={data.result.accuracy_pct} />
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {data.result.correct} right · {data.result.incorrect} wrong ·
                {' '}{data.total_questions - data.result.attempted} not answered
              </span>
            </div>
          )}

          <Made made={data.made_of} />

          {(data.chapters || []).length > 0 && (
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted)' }}>
              From: {data.chapters.map(c => c.name).join(' · ')}
            </div>
          )}
        </div>
      )}

      <div className="card">
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Past daily tests</h2>
        {past.length === 0 ? <div className="empty-state">No daily tests taken yet.</div> : (
          <table className="table">
            <thead><tr><th>Date</th><th>Questions</th><th style={{ textAlign: 'right' }}>Score</th></tr></thead>
            <tbody>
              {past.slice(0, 40).map(d => (
                <tr key={d.daily_test_id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/student/test/${d.test_id}/result`)}>
                  <td>{new Date(d.date).toLocaleDateString()}</td>
                  <td>{d.total_questions}</td>
                  <td style={{ textAlign: 'right' }}><ScorePill pct={d.result?.accuracy_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
